import { useCallback, useEffect, useState } from "react";
import {
  PlayerCard,
  PlayerCardTemplate,
  PlayerCardTheme,
  PolymorphicPlayerCard,
  PlayerCardFilters,
  PlayerCardUpdateRequestBody,
  PlayerCardServices,
  PlayerCardSortColumn,
  PlayerCardTemplateRequestBody,
  PlayerCardCreateRequestBody,
} from "../../../services/bedrock";
import { ToastContextData } from "./ToastContext";
import { DeepPartial, usePlayerCardFetch } from "../../../utils";
import pluralize from "pluralize";

export interface PlayerCardsContextData {
  playerCards: PlayerCard[];
  isLoading: boolean;
  activePage: number;
  totalPages: number;
  selectedPlayerCard: PlayerCard | undefined;
  setSelectedPlayerCard: React.Dispatch<React.SetStateAction<PlayerCard | undefined>>;
  inEditMode: boolean;
  setInEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  filters?: PlayerCardFilters;
  setFilter: (filter: keyof PlayerCardFilters, value: number | number[] | undefined) => void;
  sortColumn?: PlayerCardSortColumn;
  sortOrder: "asc" | "desc";
  setSort: (sortColumn: PlayerCardSortColumn) => void;
  loadPage: (page?: number) => void;
  createPlayerCard: (requestBody?: PlayerCardCreateRequestBody) => Promise<PlayerCard[]>;
  updatePolymorphicPlayerCard: (
    playerCard: PolymorphicPlayerCard,
    changes: DeepPartial<PolymorphicPlayerCard>,
    sendNotification?: boolean
  ) => Promise<PolymorphicPlayerCard>;
  savePlayerCardSlug: (playerCardId: number, slug: string) => Promise<PlayerCard>;
  savePlayerCardTheme: (playerCardId: number, theme: PlayerCardTheme) => Promise<PlayerCard>;
  deletePlayerCard: (playerCardId: number) => Promise<void>;
  grantPlayerCardAccess: (playerCard: PlayerCard, sendNotification: boolean) => Promise<void>;
  handlePlayerCardUpdated: (playerCard: PlayerCard) => void;
  handleTemplateUpdated: (template: PlayerCardTemplate) => void;
  handleThemeUpdated: (theme: PlayerCardTheme) => void;
}

export const InitialPlayerCardsContextData: PlayerCardsContextData = {
  playerCards: [],
  isLoading: true,
  activePage: 1,
  totalPages: 1,
  selectedPlayerCard: undefined,
  setSelectedPlayerCard: () => {},
  inEditMode: false,
  setInEditMode: () => {},
  filters: undefined,
  setFilter: () => {},
  sortColumn: undefined,
  sortOrder: "asc",
  setSort: (_: string) => {},
  loadPage: (_?: number) => {},
  createPlayerCard: (_?: PlayerCardCreateRequestBody) => Promise.reject("Not implemented"),
  updatePolymorphicPlayerCard: (_?: PolymorphicPlayerCard, __?: DeepPartial<PolymorphicPlayerCard>) =>
    Promise.reject("Not implemented"),
  savePlayerCardSlug: (_: number, __: string) => Promise.reject("Not implemented"),
  savePlayerCardTheme: (_: number, __: PlayerCardTheme) => Promise.reject("Not implemented"),
  deletePlayerCard: (_: number) => Promise.reject("Not implemented"),
  grantPlayerCardAccess: (_: PlayerCard, __: boolean) => Promise.reject("Not implemented"),
  handlePlayerCardUpdated: (_: PlayerCard) => {},
  handleTemplateUpdated: (_: PlayerCardTemplate) => {},
  handleThemeUpdated: (_: PlayerCardTheme) => {},
};

export const PlayerCardsContext = (Toast: ToastContextData): PlayerCardsContextData => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [playerCards, setPlayerCards] = useState<PlayerCard[]>([]);
  const [selectedPlayerCard, setSelectedPlayerCard] = useState<PlayerCard>();
  const [inEditMode, setInEditMode] = useState(false);
  const [sortColumn, setSortColumn] = useState<PlayerCardSortColumn>();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [playerCardFilters, setPlayerCardFilters] = useState<PlayerCardFilters>(() => {
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      const filterPlayerId = searchParams.get("playerId");
      return filterPlayerId ? { playerId: Number(filterPlayerId) } : {};
    }
    return {};
  });
  const { fetchPlayerCards, getCachedPlayerCards, clearPlayerCardsCache } = usePlayerCardFetch();

  const applyPageResponse = useCallback((response: { content: PlayerCard[]; pagination: { totalPages: number } }) => {
    setPlayerCards(response.content);
    setTotalPages(response.pagination.totalPages);
    setSelectedPlayerCard(prev => {
      if (prev) {
        const index = response.content.findIndex(playerCard => playerCard.id === prev.id);
        if (index !== -1) {
          return response.content[index];
        }
      }
      if (typeof window !== "undefined") {
        const searchParams = new URLSearchParams(window.location.search);
        const editModeParam = searchParams.get("edit");
        if (editModeParam === "true" && response.content.length > 0) {
          setInEditMode(true);
          return response.content[0];
        }
      }
      return prev || response.content[0];
    });
  }, []);

  const loadPage = useCallback(
    (page?: number, filters?: PlayerCardFilters, column?: PlayerCardSortColumn, order?: "asc" | "desc") => {
      if (page !== undefined) setActivePage(page);
      else page = activePage;
      if (filters !== undefined) setPlayerCardFilters(filters);
      else filters = playerCardFilters;
      if (column !== undefined) setSortColumn(column);
      else column = sortColumn;
      if (order !== undefined) setSortOrder(order);
      else order = sortOrder;

      const params = { page, perPage: 15, filters, sort: column, order };
      const cached = getCachedPlayerCards(params);
      if (cached) {
        applyPageResponse(cached);
        return;
      }

      setIsLoading(true);
      fetchPlayerCards(params)
        .then(applyPageResponse)
        .catch(e => Toast.showError(e, "Failed to load Player Cards."))
        .finally(() => {
          setIsLoading(false);
        });
    },
    [activePage, playerCardFilters, sortColumn, sortOrder, fetchPlayerCards, getCachedPlayerCards, applyPageResponse]
  );
  useEffect(loadPage, []);

  const setFilter = useCallback(
    (filter: keyof PlayerCardFilters, value: number | number[] | undefined) => {
      loadPage(undefined, { ...playerCardFilters, [filter]: value });
    },
    [playerCardFilters]
  );

  const setSort = useCallback(
    (column: PlayerCardSortColumn) => {
      if (sortColumn === column) {
        loadPage(undefined, undefined, undefined, sortOrder === "asc" ? "desc" : "asc");
      } else {
        loadPage(1, undefined, column);
      }
    },
    [sortColumn, sortOrder]
  );

  const createPlayerCard = async (requestBody?: PlayerCardCreateRequestBody) => {
    requestBody = requestBody ?? {};
    const cardsText = pluralize("Card", requestBody.playerIds?.length ?? 1);
    try {
      const newPlayerCards = await PlayerCardServices.createPlayerCard(requestBody);
      clearPlayerCardsCache();
      Toast.showSuccess(`Player ${cardsText} created successfully!`);
      loadPage();
      if (newPlayerCards.length === 1) {
        setSelectedPlayerCard(newPlayerCards[0]);
        setInEditMode(true);
      }
      return newPlayerCards;
    } catch (e) {
      Toast.showError(e, `Failed to create Player ${cardsText}.`);
      throw e;
    }
  };

  const updatePolymorphicPlayerCard = async (
    playerCard: PolymorphicPlayerCard,
    changes: DeepPartial<PolymorphicPlayerCard>,
    sendNotification?: boolean
  ) => {
    try {
      var updatedPlayerCard: PolymorphicPlayerCard;
      if (playerCard.isTemplate) {
        updatedPlayerCard = await PlayerCardServices.updateTemplate(
          playerCard.id,
          changes as PlayerCardTemplateRequestBody
        );
      } else {
        const updateChanges = changes as PlayerCardUpdateRequestBody;
        updateChanges.sendNotification = sendNotification;
        updatedPlayerCard = await PlayerCardServices.updatePlayerCard(playerCard.id, updateChanges);
        clearPlayerCardsCache();
      }
      Toast.showSuccess(`Player Card${playerCard.isTemplate ? " template" : ""} updated successfully!`);
      return updatedPlayerCard;
    } catch (e) {
      Toast.showError(e, "Failed to update Player Card.");
      throw e;
    }
  };

  const savePlayerCardSlug = async (playerCardId: number, slug: string) => {
    try {
      const playerCard = await PlayerCardServices.updatePlayerCard(playerCardId, { slug });
      handlePlayerCardUpdated(playerCard);
      Toast.showSuccess("Theme updated successfully!");
      return playerCard;
    } catch (e) {
      Toast.showError(e, "Failed to update Player Card.");
      throw e;
    }
  };

  const savePlayerCardTheme = async (playerCardId: number, theme: PlayerCardTheme) => {
    try {
      const playerCard = await PlayerCardServices.updatePlayerCard(playerCardId, { themeId: theme.id });
      clearPlayerCardsCache();
      setSelectedPlayerCard(prev => {
        if (prev?.id === playerCardId) {
          return playerCard;
        } else {
          return prev;
        }
      });
      Toast.showSuccess("Theme updated successfully!");
      return playerCard;
    } catch (e) {
      Toast.showError(e, "Failed to save Player Card theme.");
      throw e;
    }
  };

  const deletePlayerCard = async (playerCardId: number) => {
    try {
      setIsLoading(true);
      await PlayerCardServices.deletePlayerCard(playerCardId);
      clearPlayerCardsCache();
      setIsLoading(false);
      loadPage();
      Toast.showSuccess("Player Card deleted successfully!");
    } catch (e) {
      Toast.showError(e, "Failed to delete Player Card.");
      throw e;
    }
  };

  const grantPlayerCardAccess = async (playerCard: PlayerCard, sendNotification: boolean) => {
    try {
      setIsLoading(true);
      await PlayerCardServices.updatePlayerCard(playerCard.id, { grantPlayerCardAccess: true, sendNotification });
      clearPlayerCardsCache();
      setIsLoading(false);
      loadPage();
      Toast.showSuccess("Player Card access granted successfully!");
    } catch (e) {
      Toast.showError(e, "Failed to grant Player Card access to player.");
      setIsLoading(false);
      throw e;
    }
  };

  const handlePlayerCardUpdated = (playerCard: PlayerCard) => {
    clearPlayerCardsCache();
    setPlayerCards(prev => {
      const index = prev.findIndex(pc => pc.id === playerCard.id);
      if (index >= 0) {
        return [...prev.slice(0, index), playerCard, ...prev.slice(index + 1)];
      } else {
        return prev;
      }
    });
    setSelectedPlayerCard(prev => {
      if (prev?.id === playerCard.id) {
        return playerCard;
      } else {
        return prev;
      }
    });
  };

  const handleTemplateUpdated = (template: PlayerCardTemplate) => {
    clearPlayerCardsCache();
    setPlayerCards(prev => {
      var updated = false;
      const newValue = prev.map(pc => {
        if (pc.template?.id === template.id) {
          updated = true;
          return { ...pc, template, templateId: template.id };
        } else {
          return pc;
        }
      });
      if (updated) {
        return newValue;
      } else {
        return prev;
      }
    });
    setSelectedPlayerCard(prev => {
      if (prev?.template?.id === template.id) {
        return { ...prev, template, templateId: template.id };
      } else {
        return prev;
      }
    });
  };

  const handleThemeUpdated = (theme: PlayerCardTheme) => {
    clearPlayerCardsCache();
    setPlayerCards(prev => {
      var updated = false;
      const newValue = prev.map(pc => {
        if (pc.theme?.id === theme.id) {
          updated = true;
          return { ...pc, theme, themeId: theme.id };
        } else {
          return pc;
        }
      });
      if (updated) {
        return newValue;
      } else {
        return prev;
      }
    });
    setSelectedPlayerCard(prev => {
      if (prev?.theme?.id === theme.id) {
        return { ...prev, theme, themeId: theme.id };
      } else {
        return prev;
      }
    });
  };

  return {
    playerCards,
    isLoading,
    activePage,
    totalPages,
    selectedPlayerCard,
    setSelectedPlayerCard,
    inEditMode,
    setInEditMode,
    filters: playerCardFilters,
    setFilter,
    sortColumn,
    sortOrder,
    setSort,
    loadPage,
    createPlayerCard,
    updatePolymorphicPlayerCard,
    savePlayerCardSlug,
    savePlayerCardTheme,
    deletePlayerCard,
    grantPlayerCardAccess,
    handlePlayerCardUpdated,
    handleTemplateUpdated,
    handleThemeUpdated,
  };
};
